import axios from "axios";
import { baseUrl } from "../util";

const getConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
  },
});

const getMultipartConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "multipart/form-data",
  },
});

export const partnershipService = {
  // Category endpoints
  createCategory: async (categoryData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/categories/`,
        categoryData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating category:", error);
      throw error;
    }
  },

  getCategories: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/categories/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  },

  updateCategory: async (id, categoryData) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/business/categories/${id}/`,
        categoryData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  },

  deleteCategory: async (id) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/api/business/categories/${id}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  },

  // Partnership endpoints
  getPartnerships: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching partnerships:", error);
      throw error;
    }
  },

  getPartnershipsByCategory: async (categoryId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/by_category/?category_id=${categoryId}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching partnerships by category:", error);
      throw error;
    }
  },

  getMyPartnerships: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/my_partnerships/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching my partnerships:", error);
      throw error;
    }
  },

  getPendingPartnerships: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/pending/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pending partnerships:", error);
      throw error;
    }
  },

  getFeaturedPartnerships: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/featured/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching featured partnerships:", error);
      throw error;
    }
  },

  getPartnershipsWithYfsDiscount: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/with_yfs_discount/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching partnerships with YFS discount:", error);
      throw error;
    }
  },

  getPartnershipsWithFreeShipping: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/with_free_shipping/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching partnerships with free shipping:", error);
      throw error;
    }
  },

  getPartnership: async (id) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/${id}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching partnership ${id}:`, error);
      throw error;
    }
  },

  createPartnership: async (partnershipData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/`,
        partnershipData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating partnership:", error);
      throw error;
    }
  },

  updatePartnership: async (id, partnershipData) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/business/partnerships/${id}/update_partnership/`,
        partnershipData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating partnership ${id}:`, error);
      throw error;
    }
  },

  partialUpdatePartnership: async (id, partnershipData) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/business/partnerships/${id}/partial_update_partnership/`,
        partnershipData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error partially updating partnership ${id}:`, error);
      throw error;
    }
  },

  deletePartnership: async (id) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/api/business/partnerships/${id}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting partnership ${id}:`, error);
      throw error;
    }
  },

  approvePartnership: async (id, categoryId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/${id}/approve/`,
        { category_id: categoryId },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error approving partnership ${id}:`, error);
      throw error;
    }
  },

  rejectPartnership: async (id) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/${id}/reject/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error rejecting partnership ${id}:`, error);
      throw error;
    }
  },

  toggleFeature: async (id) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/${id}/feature/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error toggling feature status for partnership ${id}:`,
        error
      );
      throw error;
    }
  },

  createPartnershipWithDocuments: async (
    partnershipData,
    documentFiles,
    documentLinks,
    documentLinkTexts,
    documentInputTypes
  ) => {
    // Create form data for multipart/form-data submission
    const formData = new FormData();

    // Add partnership data
    Object.keys(partnershipData).forEach((key) => {
      if (key === "shipping_countries" && Array.isArray(partnershipData[key])) {
        // Convert shipping_countries array to JSON string
        formData.append(key, JSON.stringify(partnershipData[key]));
      } else {
        formData.append(key, partnershipData[key]);
      }
    });

    // Process documents and their metadata
    const documentTypes = [];
    const documentNames = [];
    const documentFilesArray = [];
    const documentLinksArray = [];
    const documentLinkTextsArray = [];

    // Handle file uploads
    Object.entries(documentFiles).forEach(([type, file]) => {
      if (file && documentInputTypes && documentInputTypes[type] === "file") {
        documentFilesArray.push(file);
        documentTypes.push(type);
        documentNames.push(file.name);
      }
    });

    // Handle link inputs
    if (documentLinks) {
      Object.entries(documentLinks).forEach(([type, link]) => {
        if (link && documentInputTypes && documentInputTypes[type] === "link") {
          documentLinksArray.push(link);
          documentLinkTextsArray.push(documentLinkTexts[type] || "");
          documentTypes.push(type);
        }
      });
    }

    // Add document files
    documentFilesArray.forEach((file) => {
      formData.append("documents", file);
    });

    // Add document metadata arrays - this ensures proper order alignment
    documentTypes.forEach((type) => {
      formData.append("document_types", type);
    });

    documentNames.forEach((name) => {
      formData.append("document_names", name);
    });

    // Add document links and link texts
    documentLinksArray.forEach((link) => {
      formData.append("document_links", link);
    });

    documentLinkTextsArray.forEach((linkText) => {
      formData.append("document_link_texts", linkText);
    });

    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/create_with_documents/`,
        formData,
        getMultipartConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating partnership with documents:", error);
      throw error;
    }
  },

  createPartnershipWithDocumentsAndProducts: async (
    partnershipData,
    documentFiles,
    featuredProducts,
    documentLinks,
    documentLinkTexts,
    documentInputTypes
  ) => {
    // Create form data for multipart/form-data submission
    const formData = new FormData();

    // Add partnership data
    Object.keys(partnershipData).forEach((key) => {
      if (key === "shipping_countries" && Array.isArray(partnershipData[key])) {
        // Convert shipping_countries array to JSON string
        formData.append(key, JSON.stringify(partnershipData[key]));
      } else {
        formData.append(key, partnershipData[key]);
      }
    });

    // Process documents and their metadata
    const fileDocumentTypes = [];
    const fileDocumentNames = [];
    const documentFilesArray = [];
    const linkDocumentTypes = [];
    const documentLinksArray = [];
    const documentLinkTextsArray = [];

    // Handle file uploads first
    Object.entries(documentFiles).forEach(([type, file]) => {
      if (file && documentInputTypes[type] === "file") {
        documentFilesArray.push(file);
        fileDocumentTypes.push(type);
        fileDocumentNames.push(file.name);
      }
    });

    // Handle link inputs second
    Object.entries(documentLinks).forEach(([type, link]) => {
      if (link && documentInputTypes[type] === "link") {
        documentLinksArray.push(link);
        linkDocumentTypes.push(type);
        documentLinkTextsArray.push(documentLinkTexts[type] || "");
      }
    });

    // Add document files
    documentFilesArray.forEach((file) => {
      formData.append("documents", file);
    });

    // Add document links
    documentLinksArray.forEach((link) => {
      formData.append("document_links", link);
    });

    // Add document types in the same order as documents and links
    fileDocumentTypes.forEach((type) => {
      formData.append("document_types", type);
    });
    linkDocumentTypes.forEach((type) => {
      formData.append("document_types", type);
    });

    // Add document names
    fileDocumentNames.forEach((name) => {
      formData.append("document_names", name);
    });

    // Add document link texts
    documentLinkTextsArray.forEach((linkText) => {
      formData.append("document_link_texts", linkText);
    });

    // Add featured products data
    featuredProducts.forEach((product, index) => {
      formData.append(`featured_product_names`, product.product_name);
      formData.append(
        `featured_product_descriptions`,
        product.product_description
      );

      // Add the actual image file
      if (product.image) {
        formData.append(`featured_product_images`, product.image);
      }

      // Add LCA calculation file
      if (product.lca_calculation) {
        formData.append(
          `featured_product_lca_calculations`,
          product.lca_calculation
        );
      } else {
        // Always append, even if empty, to keep order
        formData.append(`featured_product_lca_calculations`, "");
      }

      // Add CO2e value
      formData.append(`featured_product_co2es`, product.co2e || "");

      // Add individual store price flag
      formData.append(
        `featured_product_individual_store_prices`,
        product.individual_store_price ? "true" : "false"
      );

      // Only add price, unit, and unit_qty if individual store price is not checked
      if (!product.individual_store_price) {
        formData.append(`featured_product_prices`, product.price);
        formData.append(`featured_product_units`, product.unit);
        formData.append(`featured_product_unit_qtys`, product.unit_qty);
      } else {
        // Add empty values for price fields when individual store price is checked
        formData.append(`featured_product_prices`, "");
        formData.append(`featured_product_units`, "");
        formData.append(`featured_product_unit_qtys`, "");
      }
    });

    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/create_with_documents_and_products/`,
        formData,
        getMultipartConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error creating partnership with documents and products:",
        error
      );
      throw error;
    }
  },

  // Partnership Document endpoints
  getPartnershipDocuments: async (partnershipId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/${partnershipId}/documents/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching documents for partnership ${partnershipId}:`,
        error
      );
      throw error;
    }
  },

  uploadDocument: async (partnershipId, documentData) => {
    const formData = new FormData();
    formData.append("document_type", documentData.documentType);
    formData.append("file", documentData.file);
    formData.append("file_name", documentData.fileName);

    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/${partnershipId}/documents/`,
        formData,
        getMultipartConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error uploading document for partnership ${partnershipId}:`,
        error
      );
      throw error;
    }
  },

  updateDocument: async (partnershipId, documentId, documentData) => {
    const formData = new FormData();
    if (documentData.documentType) {
      formData.append("document_type", documentData.documentType);
    }
    if (documentData.file) {
      formData.append("file", documentData.file);
    }
    if (documentData.fileName) {
      formData.append("file_name", documentData.fileName);
    }

    try {
      const response = await axios.put(
        `${baseUrl}/api/business/partnerships/${partnershipId}/documents/${documentId}/`,
        formData,
        getMultipartConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating document ${documentId}:`, error);
      throw error;
    }
  },

  deleteDocument: async (partnershipId, documentId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/api/business/partnerships/${partnershipId}/documents/${documentId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting document ${documentId}:`, error);
      throw error;
    }
  },

  // Featured Product endpoints
  getFeaturedProducts: async (partnershipId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/${partnershipId}/featured-products/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching featured products for partnership ${partnershipId}:`,
        error
      );
      throw error;
    }
  },

  getFeaturedProductsByPartnership: async (partnershipId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/business/partnerships/${partnershipId}/featured-products/by_partnership/?partnership_id=${partnershipId}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching featured products by partnership ${partnershipId}:`,
        error
      );
      throw error;
    }
  },

  createFeaturedProduct: async (partnershipId, productData) => {
    const formData = new FormData();

    // Add product data
    formData.append("product_name", productData.product_name);
    formData.append("product_description", productData.product_description);

    // Add image file if it exists
    if (productData.image) {
      formData.append("image", productData.image);
    }

    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/${partnershipId}/featured-products/`,
        formData,
        getMultipartConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating featured product:", error);
      throw error;
    }
  },

  updateFeaturedProduct: async (partnershipId, productId, productData) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/business/partnerships/${partnershipId}/featured-products/${productId}/`,
        productData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating featured product ${productId}:`, error);
      throw error;
    }
  },

  deleteFeaturedProduct: async (partnershipId, productId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/api/business/partnerships/${partnershipId}/featured-products/${productId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting featured product ${productId}:`, error);
      throw error;
    }
  },

  sendYFSSpecialOffer: async (data) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/business/partnerships/send-yfs-special-offer/`,
        data,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error sending YFS special offer:", error);
      throw error;
    }
  },
};

export default partnershipService;
